import React, { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import TreeNode from './TreeNode';
import { Typography } from '@material-ui/core';
import ReactDOM from 'react-dom';
import { useTranslation } from '../hooks/useTranslation';

const useStyles = makeStyles((theme) => ({
  treeContainer: {
    width: '100%',
    backgroundColor: 'var(--ikaro-text-white)',
    borderRadius: 4,
    padding: theme.spacing(2),
    overflowY: 'auto',
    overflowX: 'auto',
    height: '100%',
    boxSizing: 'border-box',
    position: 'relative'
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
    color: theme.palette.text.secondary
  },
  treeContent: {
    minWidth: 'fit-content',
    maxWidth: 'max-content',
    paddingBottom: theme.spacing(2)
  },
  nodeSpacing: {
    height: 2,
    margin: 0,
    borderRadius: 0,
    transition: 'background-color 0.1s',
    opacity: 0,
    '&:hover': {
      opacity: 0.3,
      backgroundColor: 'rgba(33, 150, 243, 0.1)',
      height: 4
    }
  },
  ghostContainer: {
    position: 'fixed',
    pointerEvents: 'none',
    zIndex: 9999,
    width: 'auto',
    opacity: 0.8,
    borderRadius: '4px',
    backgroundColor: 'var(--ikaro-text-white)',
    padding: '6px 12px',
    boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
    border: '1px solid #ccc'
  }
}));

// Função para encontrar um nó pelo id
const findNodeById = (nodes, id) => {
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].id === id) {
      return nodes[i];
    }
    if (nodes[i].children && nodes[i].children.length > 0) {
      const foundInChild = findNodeById(nodes[i].children, id);
      if (foundInChild) {
        return foundInChild;
      }
    }
  }
  return null;
};

// Função para encontrar o nó pai de um nó
const findParentNode = (nodes, id, parent = null) => {
  for (const node of nodes) {
    if (node.id === id) {
      return parent;
    }
    if (node.children && node.children.length > 0) {
      const found = findParentNode(node.children, id, node);
      if (found) return found;
    }
  }
  return null;
};

// Função para verificar se um nó é descendente de outro
const isDescendantOf = (nodes, nodeId, potentialAncestorId) => {
  const potentialAncestor = findNodeById(nodes, potentialAncestorId);
  if (!potentialAncestor) return false;
  
  if (potentialAncestor.children) {
    for (const child of potentialAncestor.children) {
      if (child.id === nodeId) return true;
      if (isDescendantOf([child], nodeId, child.id)) return true;
    }
  }
  
  return false;
};

// Função para adicionar um nó filho a um nó específico
const addChildToNode = (nodes, parentId, newNode) => {
  return nodes.map(node => {
    if (node.id === parentId) {
      // Adicionar novo filho à lista de filhos do nó pai
      return {
        ...node,
        children: [...node.children, newNode]
      };
    } else if (node.children && node.children.length > 0) {
      // Procurar nos filhos
      return {
        ...node,
        children: addChildToNode(node.children, parentId, newNode)
      };
    }
    // Retornar o nó original se não for o nó pai nem tenha filhos
    return node;
  });
};

// Função para remover um nó específico
const removeNodeById = (nodes, id) => {
  return nodes.filter(node => {
    if (node.id === id) {
      return false;
    }
    if (node.children && node.children.length > 0) {
      node.children = removeNodeById(node.children, id);
    }
    return true;
  });
};

// Função para atualizar um nó específico
const updateNodeById = (nodes, id, data) => {
  return nodes.map(node => {
    if (node.id === id) {
      // Atualizar os dados do nó
      return {
        ...node,
        ...data
      };
    } else if (node.children && node.children.length > 0) {
      // Buscar recursivamente nos filhos
      return {
        ...node,
        children: updateNodeById(node.children, id, data)
      };
    }
    // Retornar o nó original
    return node;
  });
};

// Função para mover um nó como irmão de outro nó (no mesmo nível)
const moveSibling = (nodes, sourceId, targetId, position = 'after') => {
  // Encontrar o nó a ser movido
  const sourceNode = findNodeById(nodes, sourceId);
  if (!sourceNode) return nodes;
  
  // Encontrar o nó alvo
  const targetNode = findNodeById(nodes, targetId);
  if (!targetNode) return nodes;
  
  // Encontrar o pai do nó alvo
  const targetNodeParent = findParentNode(nodes, targetId);
  
  // Remover o nó fonte da sua posição atual
  let nodesWithoutSource = removeNodeById(nodes, sourceId);
  
  // Se o nó alvo é um nó raiz, então movemos para a raiz
  if (!targetNodeParent) {
    // Encontrar o índice do nó alvo na raiz
    const targetIndex = nodes.findIndex(node => node.id === targetId);
    if (targetIndex === -1) return nodes;
    
    // Inserir o nó fonte antes ou depois do nó alvo, dependendo da posição
    if (position === 'before') {
      return [
        ...nodesWithoutSource.slice(0, targetIndex),
        sourceNode,
        ...nodesWithoutSource.slice(targetIndex)
      ];
    } else { // 'after'
      return [
        ...nodesWithoutSource.slice(0, targetIndex + 1),
        sourceNode,
        ...nodesWithoutSource.slice(targetIndex + 1)
      ];
    }
  }
  
  // O nó alvo não é raiz, então o nó fonte deve ser movido para ser irmão do nó alvo
  // Função para adicionar o nó fonte como irmão do nó alvo
  const addAsSibling = (nodes, targetId, sourceNode, position) => {
    const result = [];
    
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].id === targetId) {
        // Inserir antes ou depois dependendo da posição
        if (position === 'before') {
          result.push(sourceNode);
          result.push(nodes[i]);
        } else { // 'after'
          result.push(nodes[i]);
          result.push(sourceNode);
        }
      } else {
        const node = { ...nodes[i] };
        result.push(node);
        
        // Processar recursivamente os filhos
        if (node.children && node.children.length > 0) {
          node.children = addAsSibling(node.children, targetId, sourceNode, position);
        }
      }
    }
    
    return result;
  };
  
  return addAsSibling(nodesWithoutSource, targetId, sourceNode, position);
};



const CustomTree = ({ 
  treeData, 
  onNodeAdd,
  onNodeRemove,
  onNodeEdit,
  onNodeEditOpen,
  onDragDrop
}) => {
  const classes = useStyles();
  const { t } = useTranslation('editor');
  const [dragNode, setDragNode] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [dropPosition, setDropPosition] = useState(null); // 'inside', 'before', 'after'
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const dragNodeRef = useRef(null);
  const ghostRef = useRef(null);
  
  // Criar portal para o elemento fantasma
  const portalRoot = useRef(null);
  
  useEffect(() => {
    // Criar o elemento para o portal do fantasma
    portalRoot.current = document.createElement('div');
    portalRoot.current.classList.add('ghost-portal');
    document.body.appendChild(portalRoot.current);
    
    // Adicionar evento para rastrear a posição do mouse
    const handleMouseMove = (e) => {
      if (dragNodeRef.current) {
        setMousePosition({ x: e.clientX, y: e.clientY });
      }
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      // Limpar
      document.removeEventListener('mousemove', handleMouseMove);
      if (portalRoot.current) {
        document.body.removeChild(portalRoot.current);
      }
    };
  }, []);
  
  useEffect(() => {
    if (dragNode && dropTarget && dragNode !== dropTarget) {
      // Processamento baseado na posição de drop
      if (dropPosition === 'inside') {
        // Verificar se não estamos tentando dropar um nó dentro de si mesmo ou em um de seus descendentes
        if (isDescendantOf(treeData, dropTarget, dragNode)) {
          console.log("Cannot move a node inside one of its descendants");
        } else {
          // Mover como filho
          onDragDrop(dragNode, dropTarget, 'inside');
        }
      } else {
        // Mover como irmão (antes ou depois)
        onDragDrop(dragNode, dropTarget, dropPosition);
      }
      
      // Resetar os estados de drag e drop
      setDragNode(null);
      setDropTarget(null);
      setDropPosition(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDragDrop]);

  const handleDragStart = (nodeId, e) => {
    setDragNode(nodeId);
    dragNodeRef.current = nodeId;
    
    // Usar uma imagem transparente como fantasma padrão para o sistema nativo
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(img, 0, 0);
    
    e.dataTransfer.setData('text/plain', nodeId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (nodeId, e, position) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Definir a posição de drop baseada na posição do cursor dentro do nó alvo
    if (position === 'auto') {
      const rect = e.currentTarget.getBoundingClientRect();
      const relativeY = e.clientY - rect.top;
      
      if (relativeY < rect.height * 0.3) {
        setDropPosition('before');
      } else if (relativeY > rect.height * 0.7) {
        setDropPosition('after');
      } else {
        setDropPosition('inside');
      }
    } else if (position === 'before' || position === 'after' || position === 'inside') {
      setDropPosition(position);
    }
    
    setDropTarget(nodeId);
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (nodeId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (dragNodeRef.current && nodeId !== dragNodeRef.current) {
      const dragId = dragNodeRef.current;
      
      // Realizar a operação de drop usando a posição atual
      if (dropPosition === 'inside') {
        // Verificar se não estamos tentando dropar um nó dentro de si mesmo ou de seus descendentes
        if (!isDescendantOf(treeData, nodeId, dragId)) {
          onDragDrop(dragId, nodeId, 'inside');
        }
      } else {
        onDragDrop(dragId, nodeId, dropPosition);
      }
    }
    
    // Resetar estados
    setDragNode(null);
    setDropTarget(null);
    setDropPosition(null);
    dragNodeRef.current = null;
  };
  
  const handleDragEnd = () => {
    // Resetar estados
    setDragNode(null);
    setDropTarget(null);
    setDropPosition(null);
    dragNodeRef.current = null;
  };

  const renderTree = (nodes, level = 0) => {
    return nodes.map((node, index) => (
      <React.Fragment key={node.id}>
        <TreeNode
          node={node}
          level={level}
          onAdd={() => onNodeAdd(node.id)}
          onRemove={() => onNodeRemove(node.id)}
          onEdit={() => onNodeEditOpen(node)}
          onDragStart={(e) => handleDragStart(node.id, e)}
          onDragOver={(e, position) => handleDragOver(node.id, e, position)}
          onDrop={(e) => handleDrop(node.id, e)}
          onDragEnd={handleDragEnd}
          isDraggable={true}
          dropPosition={node.id === dropTarget ? dropPosition : null}
        >
          {node.children && node.children.length > 0 && renderTree(node.children, level + 1)}
        </TreeNode>
        
        {/* Área mínima para drop entre nós, útil apenas para drag and drop */}
        {index < nodes.length - 1 && (
          <div 
            className={classes.nodeSpacing}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDragOver(nodes[index + 1].id, e, 'before');
            }}
            onDrop={(e) => handleDrop(nodes[index + 1].id, e)}
            aria-hidden="true" // Indica que este elemento é apenas funcional, não visível
          />
        )}
      </React.Fragment>
    ));
  };

  return (
    <div className={classes.treeContainer}>
      {treeData && treeData.length > 0 ? (
        <div className={classes.treeContent}>
          {renderTree(treeData)}
        </div>
      ) : (
        <div className={classes.emptyContainer}>
          <Typography variant="body1" gutterBottom>
            {t('noItemsCreated')}
          </Typography>
          <Typography variant="body2">
            {t('clickButtonToStart')}
          </Typography>
        </div>
      )}
      
      {/* Renderizar o fantasma usando um portal */}
      {dragNode && ReactDOM.createPortal(
        <div 
          className={classes.ghostContainer}
          style={{
            left: `${mousePosition.x + 15}px`,
            top: `${mousePosition.y + 15}px`,
          }}
          ref={ghostRef}
        >
          {findNodeById(treeData, dragNode)?.nodeData.name || 'Sem nome'}
        </div>,
        portalRoot.current
      )}
    </div>
  );
};

export { 
  CustomTree, 
  findNodeById, 
  findParentNode, 
  addChildToNode, 
  removeNodeById, 
  updateNodeById,
  moveSibling,
  isDescendantOf
}; 